import { NavLink, useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  Headphones,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Plus,
  User,
  Users,
} from 'lucide-react';
import { Logo } from './logo';
import { UserAvatar } from '@/components/users/user-avatar';
import { useAuthStore } from '@/stores/auth-store';
import { useUiStore } from '@/stores/ui-store';
import { useLogout } from '@/hooks/use-auth';
import { slugify } from '@/lib/dates';
import { cn } from '@/lib/utils';

/* Navegação principal — espelha NAV_ITEMS do protótipo (Painel / Eventos / Posts). */
const primaryLinks = [
  { to: '/dashboard', label: 'Painel', icon: LayoutDashboard },
  { to: '/calendar', label: 'Eventos', icon: CalendarDays },
  { to: '/marketing', label: 'Posts', icon: Megaphone },
] as const;

export function DesktopSidebar() {
  const user = useAuthStore((s) => s.user);
  const openCreate = useUiStore((s) => s.openCreate);
  const logout = useLogout();
  const navigate = useNavigate();

  function handleLogout() {
    logout.mutate(undefined, {
      onSuccess: () => navigate('/login'),
    });
  }

  // "Equipe" aponta para a página por-usuário do próprio usuário logado
  // (mesmo resolvedor de slug usado em TasksByUserPage).
  const teamHref = user ? `/equipe/${slugify(user.name)}` : '/equipe';

  /* Extras — Equipe / DJs / Conta. */
  const extraLinks = [
    { to: teamHref, label: 'Equipe', icon: Users },
    { to: '/settings/djs', label: 'DJs', icon: Headphones },
    { to: '/settings', label: 'Conta', icon: User },
  ] as const;

  // Classes compartilhadas por item de navegação (ativo x inativo).
  const itemClass = (isActive: boolean) =>
    cn(
      'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
      isActive
        ? 'bg-primary-soft text-primary'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
    );

  return (
    <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:w-[248px] lg:flex-col h-screen sticky top-0 border-r border-border bg-card z-30">
      {/* Topo — logo + eyebrow */}
      <div className="px-5 pt-6 pb-5">
        <Logo size="sm" />
        <p className="eyebrow mt-3">painel interno</p>
      </div>

      {/* Botão Criar */}
      <div className="px-3 pb-2">
        <button
          type="button"
          onClick={openCreate}
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground shadow-red transition active:scale-[0.97]"
        >
          <Plus className="h-4 w-4" strokeWidth={2.5} />
          Criar
        </button>
      </div>

      {/* Navegação */}
      <nav className="px-3 flex-1 overflow-y-auto">
        <ul className="space-y-1">
          {primaryLinks.map((l) => {
            const Icon = l.icon;
            return (
              <li key={l.to}>
                <NavLink to={l.to} className={({ isActive }) => itemClass(isActive)}>
                  {({ isActive }) => (
                    <>
                      <Icon className="h-4 w-4" strokeWidth={isActive ? 2 : 1.75} />
                      {l.label}
                      {isActive && (
                        <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>

        {/* Seção de extras */}
        <p className="eyebrow px-3 mt-6 mb-2">geral</p>
        <ul className="space-y-1">
          {extraLinks.map((l) => {
            const Icon = l.icon;
            return (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.to === '/settings'}
                  className={({ isActive }) => itemClass(isActive)}
                >
                  {({ isActive }) => (
                    <>
                      <Icon className="h-4 w-4" strokeWidth={isActive ? 2 : 1.75} />
                      {l.label}
                      {isActive && (
                        <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Rodapé — avatar + nome + sair */}
      {user && (
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-1 pb-3">
            <UserAvatar
              name={user.name}
              colorHex={user.color_hex}
              url={user.avatar_url}
              size="md"
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{user.name}</p>
              <p className="eyebrow truncate">{user.role}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sair
          </button>
        </div>
      )}
    </aside>
  );
}
